/**
 * BLRRD Path Editor — model.js  (Phase 4.G.vector, Wave 12.2)
 *
 * Cubic-Bezier path data model. A path is an ordered list of nodes; each
 * node carries an `anchor` point plus two control handles (`handleIn`,
 * `handleOut`) expressed in ABSOLUTE image-pixel coordinates (not deltas).
 * Between two consecutive nodes A→B the curve is the cubic Bezier with
 * control points:
 *
 *     p0 = A.anchor
 *     p1 = A.handleOut
 *     p2 = B.handleIn
 *     p3 = B.anchor
 *
 * A path with `closed = true` additionally joins the LAST node back to the
 * FIRST with the same handle convention (lastNode.handleOut → firstNode
 * .handleIn).
 *
 * Why absolute handle coordinates (not relative deltas):
 *   The de-Casteljau evaluator (bezier.js) consumes raw control points;
 *   keeping handles absolute means the evaluator never has to know about
 *   the anchor it belongs to, and the UI drag math (handle = pointer
 *   position) is a direct assignment. Corner nodes simply set both
 *   handles equal to the anchor (zero-length tangents → straight line
 *   segment, since a cubic with p1=p0 and p2=p3 degenerates to the line
 *   p0→p3).
 *
 * Immutability: every mutator returns a NEW path object (spread copy of
 * the nodes array). This mirrors the project-wide immutable-update rule
 * and the pixel-layer model's `{ ...old, patch }` convention.
 *
 * Provenance: clean-room. Cubic-Bezier representation per de Casteljau
 * 1959 and Hughes/van Dam "Computer Graphics: Principles and Practice"
 * 3rd ed. §15 (parametric curves). NO Inkscape/GIMP/Krita source
 * consulted (those are GPL/LGPL; BLRRD is MIT).
 */

/* ── Point helpers (plain objects, defensively copied) ───────────────── */

function pt(x, y) {
    return { x: Number(x) || 0, y: Number(y) || 0 };
}

function clonePt(p) {
    return { x: p.x, y: p.y };
}

/** True when both coordinates of a point are finite. */
function finitePt(p) {
    return !!p && Number.isFinite(p.x) && Number.isFinite(p.y);
}

/* ── Node construction ───────────────────────────────────────────────── */

/**
 * Build a path node. If handles are omitted they default to the anchor
 * (a "corner" node — straight segments on both sides). Handles supplied
 * as `null` likewise collapse onto the anchor.
 *
 * @param {{x:number,y:number}} anchor
 * @param {{x:number,y:number}} [handleIn]
 * @param {{x:number,y:number}} [handleOut]
 * @returns {{anchor:object, handleIn:object, handleOut:object}}
 */
export function createNode(anchor, handleIn, handleOut) {
    if (!finitePt(anchor)) {
        throw new Error('createNode: anchor must have finite x/y');
    }
    const a = pt(anchor.x, anchor.y);
    return {
        anchor: a,
        handleIn:  finitePt(handleIn)  ? pt(handleIn.x,  handleIn.y)  : clonePt(a),
        handleOut: finitePt(handleOut) ? pt(handleOut.x, handleOut.y) : clonePt(a),
    };
}

/** True when a node's handles both coincide with its anchor (a corner). */
export function isCornerNode(node) {
    if (!node) return false;
    return node.handleIn.x === node.anchor.x && node.handleIn.y === node.anchor.y
        && node.handleOut.x === node.anchor.x && node.handleOut.y === node.anchor.y;
}

/* ── Path construction ───────────────────────────────────────────────── */

/**
 * Build an empty (or seeded) path.
 *
 * @param {Array<object>} [nodes=[]]
 * @param {boolean} [closed=false]
 * @returns {{nodes:Array<object>, closed:boolean}}
 */
export function createPath(nodes = [], closed = false) {
    const arr = Array.isArray(nodes) ? nodes.map(cloneNode) : [];
    return { nodes: arr, closed: !!closed };
}

function cloneNode(n) {
    return {
        anchor:    clonePt(n.anchor),
        handleIn:  clonePt(n.handleIn),
        handleOut: clonePt(n.handleOut),
    };
}

/** Deep copy of a path (nodes + flag). */
export function clonePath(path) {
    return { nodes: path.nodes.map(cloneNode), closed: !!path.closed };
}

/* ── Immutable mutators ──────────────────────────────────────────────── */

/** Append a node, returning a new path. */
export function appendNode(path, node) {
    return { nodes: path.nodes.concat(cloneNode(node)), closed: path.closed };
}

/** Insert a node at index `i` (clamped), returning a new path. */
export function insertNode(path, i, node) {
    const idx = Math.max(0, Math.min(path.nodes.length, i | 0));
    const next = path.nodes.slice(0, idx)
        .concat(cloneNode(node), path.nodes.slice(idx));
    return { nodes: next, closed: path.closed };
}

/** Remove the node at index `i`, returning a new path (no-op if OOB). */
export function removeNode(path, i) {
    if (i < 0 || i >= path.nodes.length) return clonePath(path);
    const next = path.nodes.slice(0, i).concat(path.nodes.slice(i + 1));
    /* A path with fewer than 2 nodes can't be closed meaningfully. */
    const closed = next.length >= 3 ? path.closed : false;
    return { nodes: next, closed };
}

/**
 * Replace the node at index `i` via an immutable patch. `patch` may
 * carry any of { anchor, handleIn, handleOut }; absent keys keep their
 * old value. When `anchor` moves, handles can optionally follow by the
 * same delta (preserves the node's tangent shape) — opt in via
 * `opts.moveHandlesWithAnchor`.
 *
 * @param {object} path
 * @param {number} i
 * @param {{anchor?:object, handleIn?:object, handleOut?:object}} patch
 * @param {{moveHandlesWithAnchor?:boolean}} [opts]
 */
export function updateNode(path, i, patch, opts = {}) {
    if (i < 0 || i >= path.nodes.length) return clonePath(path);
    const old = path.nodes[i];
    const next = cloneNode(old);

    if (patch.anchor && finitePt(patch.anchor)) {
        const dx = patch.anchor.x - old.anchor.x;
        const dy = patch.anchor.y - old.anchor.y;
        next.anchor = pt(patch.anchor.x, patch.anchor.y);
        if (opts.moveHandlesWithAnchor) {
            next.handleIn  = pt(old.handleIn.x  + dx, old.handleIn.y  + dy);
            next.handleOut = pt(old.handleOut.x + dx, old.handleOut.y + dy);
        }
    }
    if (patch.handleIn && finitePt(patch.handleIn)) {
        next.handleIn = pt(patch.handleIn.x, patch.handleIn.y);
    }
    if (patch.handleOut && finitePt(patch.handleOut)) {
        next.handleOut = pt(patch.handleOut.x, patch.handleOut.y);
    }

    const nodes = path.nodes.slice();
    nodes[i] = next;
    return { nodes, closed: path.closed };
}

/** Set the closed flag, returning a new path. Closing needs ≥3 nodes. */
export function setClosed(path, closed) {
    const c = !!closed && path.nodes.length >= 3;
    return { nodes: path.nodes.map(cloneNode), closed: c };
}

/* ── Segment enumeration ─────────────────────────────────────────────── */

/**
 * Enumerate the cubic-Bezier segments of a path. Each segment is the
 * 4-tuple of control points [p0, p1, p2, p3] (absolute coords). A path
 * of N nodes has N-1 open segments, plus 1 closing segment when closed.
 *
 * @param {object} path
 * @returns {Array<[object,object,object,object]>}
 */
export function pathSegments(path) {
    const out = [];
    const n = path.nodes;
    if (n.length < 2) return out;
    for (let i = 0; i < n.length - 1; i++) {
        out.push([n[i].anchor, n[i].handleOut, n[i + 1].handleIn, n[i + 1].anchor]);
    }
    if (path.closed && n.length >= 3) {
        const last = n[n.length - 1];
        const first = n[0];
        out.push([last.anchor, last.handleOut, first.handleIn, first.anchor]);
    }
    return out;
}

/** True when the path has no drawable segments (fewer than 2 nodes). */
export function isEmptyPath(path) {
    return !path || !Array.isArray(path.nodes) || path.nodes.length < 2;
}

/* ── Serialisation (for snapshot round-trips / debugging) ────────────── */

/** Flatten a path to a JSON-safe plain object. */
export function serializePath(path) {
    return {
        v: 1,
        closed: !!path.closed,
        nodes: path.nodes.map((n) => [
            n.anchor.x, n.anchor.y,
            n.handleIn.x, n.handleIn.y,
            n.handleOut.x, n.handleOut.y,
        ]),
    };
}

/** Rebuild a path from `serializePath` output. Tolerant of bad input. */
export function deserializePath(obj) {
    if (!obj || !Array.isArray(obj.nodes)) return createPath([], false);
    const nodes = [];
    for (const t of obj.nodes) {
        if (!Array.isArray(t) || t.length < 6) continue;
        if (!t.every(Number.isFinite)) continue;
        nodes.push({
            anchor:    { x: t[0], y: t[1] },
            handleIn:  { x: t[2], y: t[3] },
            handleOut: { x: t[4], y: t[5] },
        });
    }
    return { nodes, closed: !!obj.closed && nodes.length >= 3 };
}
